import get from 'lodash/get';
import noop from 'lodash/noop';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import join from 'lodash/join';
import tuner from '@tunein/web-tuner';
import mint from '@tunein/mint';

// actions
// utils
import isPlayable from '../utils/guideItem/isPlayable';
import getGuideItemPathname from '../utils/guideItem/getGuideItemPathname';
import {
  behaviors as GuideItemBehaviors,
  getTypeLowerCase,
  isTopic,
} from '../utils/guideItemTypes';
import { setVolume as setVolumeInStorage } from '../utils/storage/playerVolume';
import { fbPixelCustomEvents, fbTrack } from '../utils/fbTrack';
import playerStatuses from '../utils/playerStatuses';
import getPpid from '../components/ads/utils/getPpid';
import areGdprObligationsComplete from '../components/consent/utils/areGdprObligationsComplete';
import { generateCcpaStatusValue } from '../components/consent/ccpa/utils/ccpaStatus';
import { checkIsUserOptedOutAnywhere } from '../utils/optOut/checkUserOptOutStatus';
import getTunedGuideId from '../utils/guideItem/getTunedGuideId';

// selectors
import { selectBranchUrl, selectIsMobile } from '../selectors/app';
import { selectIsUserSubscribed } from '../selectors/me';
import { selectAutoPlayGuideItem } from '../selectors/player';
import { selectAutoPlayCategory } from '../selectors/categories';

// constants
import { PLAY_OPEN_IN_APP_ENABLED } from '../constants/experiments/redirect';
import { labels } from '../constants/analytics/categoryActionLabel/subscribe';
import {
  LOTAME_SEGMENT_LIMIT_DEFAULT,
  LOTAME_SEGMENT_LIMIT_KEY,
  MOBILE_PREROLL_ENABLED,
  PAUSE_PREROLL_ENABLED,
} from '../constants/experiments/ads';
import { PREROLL_DISPLAY_SLOT_NAME_AND_ID } from '../components/ads/constants/displayAds';
import { AUTOPLAY } from '../constants/containerTypes';
import { AUTOPLAY_GUIDE_ID } from '../constants/categories';
import feature from '../constants/analytics/categoryActionLabel/feature';
import { BYPASS_UPSELL_DIALOG_ENABLED } from '../constants/experiments/dialog';
import { selectExperiment } from '../selectors/config';
import createAndOpenSubscribeLink from '../components/utils/createAndOpenSubscribeLink';
import { fetchProfile } from './profile';
import { fetchCategory } from './browse';
import { openUpsellAndTrackActivity } from './dialog';
import {
  logFeatureActivity,
  logPlayStart,
  logPrerollEligible,
} from './logging';
import * as TunerActions from './tuner';

const PRE_TUNE = 'PRE_TUNE';
const REQUEST_TUNE = 'REQUEST_TUNE';
const HANDLE_LOAD = 'HANDLE_LOAD';
const PLAY = 'PLAY';
const PAUSE = 'PAUSE';
const STOP = 'STOP';
const SEEK = 'SEEK';
const SET_VOLUME = 'SET_VOLUME';
const SET_HAS_ADBLOCKER = ' SET_HAS_ADBLOCKER';
const SET_SHOW_VOLUME_BAR = 'SET_SHOW_VOLUME_BAR';
const OPEN_POPOUT_PLAYER = 'OPEN_POPOUT_PLAYER';
const SET_POPOUT_PLAYER_STATUS = 'SET_POPOUT_PLAYER_STATUS';
const UPDATE_LAST_TUNE_REQUEST = 'UPDATE_LAST_TUNE_REQUEST';
const FETCH_NOW_PLAYING = 'FETCH_NOW_PLAYING';
const AUTOPLAY_READY = 'AUTOPLAY_READY';

function handleLoad() {
  return {
    type: HANDLE_LOAD,
  };
}

function handlePlay() {
  return {
    type: PLAY,
  };
}

function handlePause() {
  return {
    type: PAUSE,
  };
}

function handleStop() {
  return {
    type: STOP,
  };
}

function handleSeek(positionPercent) {
  return {
    type: SEEK,
    positionPercent,
  };
}

function handleSetVolume(value) {
  return {
    type: SET_VOLUME,
    value,
  };
}

function isPausePrerollExperimentEnabled(state) {
  return get(state, ['config', 'autoupdate', PAUSE_PREROLL_ENABLED], false);
}

// NOTE: this is really solving for the intermediary case where a topic ends and a next one is set
// to play.
function hasNowPlayingMetaData(nowPlaying) {
  return nowPlaying && !!nowPlaying.subtitle && !!nowPlaying.title;
}

function buildDfpUrlParams(guideId) {
  // Important: URL must not redirect, so we need the trailing slash or else our route controller
  // will add one and force a redirect. https://tunein.atlassian.net/browse/TUNE-12903
  const url = `https://tunein.com/desc/${guideId}/`;
  return {
    url,
    description_url: encodeURIComponent(url),
  };
}

// exported for testing
async function buildTuneParams(state, dfpParams) {
  const lotameSegmentLimit = get(
    state,
    ['config', 'autoupdate', LOTAME_SEGMENT_LIMIT_KEY],
    LOTAME_SEGMENT_LIMIT_DEFAULT,
  );
  const lotamesegmentsArray = mint.getState('lotamesegments') || [];
  const trimmedLotameSegments = lotamesegmentsArray.slice(
    0,
    parseInt(lotameSegmentLimit, 10),
  );
  const gdprTcString = get(state, 'app.oneTrust.gdpr.tcString');

  const params = {
    ccpaStatus: generateCcpaStatusValue(),
    gdprApplies: get(state, 'app.oneTrust.gdpr.gdprApplies'),
    isOptedOut: checkIsUserOptedOutAnywhere(state),
    ppid: getPpid(state),
    dfpParams,
  };

  if (!selectIsUserSubscribed(state)) {
    params.palNonce = await mint.getPalNonce();
  }

  if (gdprTcString) {
    params.gdprTcString = gdprTcString;
  }

  if (!isEmpty(trimmedLotameSegments)) {
    params.audience = `;${join(trimmedLotameSegments, ';')};`;
  }

  return params;
}

// NOTE: exporting because it's too cumbersome to test through the tune action.  This is ok, as the
// tune action does not have branch logic based on the branch logic here.
function requestTune(playerState, params = {}) {
  const {
    tunedGuideId,
    parentGuideId,
    nowPlaying,
    guideItemPathname,
    itemToken,
  } = params;

  return {
    type: REQUEST_TUNE,
    tunedGuideId: tunedGuideId || playerState.tunedGuideId,
    parentGuideId,
    nowPlaying: hasNowPlayingMetaData(nowPlaying)
      ? nowPlaying
      : playerState.nowPlaying,
    itemToken,
    guideItemPathname,
    tuneRequestStart: Date.now(),
  };
}

function seek(positionPercent) {
  return (dispatch) => {
    tuner.seek(positionPercent);

    return dispatch(handleSeek(positionPercent));
  };
}

function play() {
  return (dispatch, getState) => {
    const state = getState();
    const prerollPlayerState = get(state, 'preroll.playerState');

    if (
      isPausePrerollExperimentEnabled(state) &&
      prerollPlayerState === playerStatuses.loaded
    ) {
      mint.play();
      return null;
    }

    tuner.play();
    return dispatch(handlePlay());
  };
}

function pause() {
  return (dispatch, getState) => {
    const state = getState();
    const prerollPlayerState = get(state, 'preroll.playerState');

    if (
      isPausePrerollExperimentEnabled(state) &&
      prerollPlayerState === playerStatuses.loaded
    ) {
      mint.pause();
      return null;
    }
    tuner.pause();
    return dispatch(handlePause());
  };
}

function stop() {
  return (dispatch) => {
    tuner.stop();
    return dispatch(handleStop());
  };
}

function setVolume(value) {
  return (dispatch) => {
    setVolumeInStorage(value);
    tuner.setVolume(value);
    return dispatch(handleSetVolume(value));
  };
}

function setShowVolumeBar(value) {
  return {
    type: SET_SHOW_VOLUME_BAR,
    value,
  };
}

function toggleMuteVolume() {
  return (dispatch, getState) => {
    const { player } = getState();

    const previousVolume = player.previousVolume || 100;
    const volume = player.volume === 0 ? previousVolume : 0;

    setVolumeInStorage(volume);
    tuner.setVolume(volume);
    return dispatch(handleSetVolume(volume));
  };
}

function primeTuneForMobileSync() {
  return (dispatch) => {
    tuner.preTune();
    return dispatch({ type: PRE_TUNE });
  };
}

function loadAndPlay(dispatch) {
  dispatch(handleLoad());
  dispatch(TunerActions.handleStreamEvaluation());
  dispatch(play());
}

async function attemptPreroll(dispatch, state) {
  const isMobile = selectIsMobile(state);
  const isMobilePrerollEnabled = get(
    state,
    ['config', 'autoupdate', MOBILE_PREROLL_ENABLED],
    false,
  );
  const { canShowAds, canShowPrerollAds, canShowVideoPrerollAds } = get(
    state,
    'player.nowPlaying.ads',
    {},
  );
  const hasMetPrerollQuota = mint.hasMetPrerollQuota();

  // preroll is disabled on mobile by default
  if (
    (!isMobile || isMobilePrerollEnabled) &&
    canShowAds &&
    canShowPrerollAds
  ) {
    dispatch(
      logPrerollEligible({
        audioEnabled: canShowPrerollAds,
        videoEnabled: canShowVideoPrerollAds,
      }),
    );

    if (!hasMetPrerollQuota) {
      // Include prerolls in sponsored ads. See ticket: REV-2537
      mint.updateState('isFirstInSession', true);
      await mint.requestSlot(PREROLL_DISPLAY_SLOT_NAME_AND_ID).catch(noop);
    }
  } else {
    dispatch(logPrerollEligible({ audioEnabled: false, videoEnabled: false }));
  }
}

/**
 * Cyclically checks for GDPR user consent selection or GDPR non-applicability before resolving;
 * one or the other is required before performing certain actions like tuning or serving ads.
 *
 * @param {Function} getState - Redux getState function.
 * @returns {Promise<undefined>} - Resolved value is thrown away.
 */
function waitForGdprObligationsToComplete(getState) {
  return new Promise((res) => {
    const interval = setInterval(() => {
      if (areGdprObligationsComplete(getState())) {
        clearInterval(interval);
        res();
      }
    }, 100);
  });
}

let lastTuneRequestOptions;

/**
 * Kicks off tune request and stream playback in web-tuner and
 * requests prerolls from mint, when applicable (via attemptPreroll()).
 *
 * @param {object} requestOptions
 * @returns {function(*, *=): Promise<undefined|null|*>}
 */
function tune(requestOptions) {
  const {
    audioClipId,
    topicGuideId,
    guideItem,
    formats,
    isAutoplayed,
    playFromPosition,
    location,
  } = requestOptions;

  // If multiple tune requests are made prior to GDPR obligations are met, lastTuneRequestOptions
  // will only match the request options of the last tune request. This will ensure that only the
  // last tune request is processed, once GDPR obligations are complete.
  lastTuneRequestOptions = requestOptions;

  /* eslint-disable consistent-return */
  return async (dispatch, getState) => {
    if (!areGdprObligationsComplete(getState())) {
      await waitForGdprObligationsToComplete(getState);
    }

    // dismisses stacked tune requests, save for the most recent
    if (requestOptions !== lastTuneRequestOptions) {
      return;
    }

    const state = getState();
    const prerollPlayerState = get(state, 'preroll.playerState');
    const playerGuideId = get(state, 'player.tunedGuideId');
    const tunedGuideId = getTunedGuideId(guideItem, audioClipId, topicGuideId);
    const isUserSubscribed = selectIsUserSubscribed(state);
    const hasMetPrerollQuota = mint.hasMetPrerollQuota();
    let parentGuideId;

    if (
      !isUserSubscribed &&
      isPausePrerollExperimentEnabled(state) &&
      tunedGuideId === playerGuideId &&
      prerollPlayerState === playerStatuses.loaded
    ) {
      mint.play();
      return null;
    }

    // regular upsell triggered by attempt to play a locked item
    // this instance should only be triggered on the large profile play button
    // because the lock tile covers other cases
    // - blocks playback
    const behaviorAction = get(guideItem, 'behaviors.primaryButton.actionName');
    if (behaviorAction === GuideItemBehaviors.subscribe) {
      if (selectExperiment(state, BYPASS_UPSELL_DIALOG_ENABLED, false)) {
        createAndOpenSubscribeLink(guideItem, location);
        return null;
      }
      return dispatch(
        openUpsellAndTrackActivity(labels.profileUpsellDialog, tunedGuideId),
      );
    }

    if (!isPlayable(guideItem)) {
      return null;
    }

    // on attempt to play, open via branch deep link
    const isMobile = selectIsMobile(state);
    const branchUrl = selectBranchUrl(state);

    const playOpenInAppStore = get(state, [
      'config',
      'autoupdate',
      PLAY_OPEN_IN_APP_ENABLED,
    ]);

    if (isMobile && playOpenInAppStore && branchUrl) {
      window.location.replace(branchUrl);
      return null;
    }

    const itemToken = get(guideItem, 'context.token');

    const isTopicGuideId = isTopic(tunedGuideId);

    const params = {
      tunedGuideId,
      nowPlaying: {
        title: isTopicGuideId ? guideItem.title : guideItem.subtitle,
        subtitle: isTopicGuideId ? guideItem.subtitle : guideItem.title,
        image: guideItem.image,
        share: get(guideItem, 'actions.share'),
        description: guideItem.description,
        slogan: get(guideItem, 'metadata.properties.station.slogan'),
      },
      guideItemPathname: getGuideItemPathname(guideItem),
      itemToken,
    };

    if (isTopicGuideId) {
      // The accompanying guide item for a topic guide ID can be either a program guide item or
      // a topic guide item. These guide items have different models for the `properties` attribute,
      // so we're treating the guide item as a topic first, then falling back to the program model.
      parentGuideId =
        get(guideItem, 'properties.parentProgram.guideId') ||
        get(guideItem, 'properties.seoInfo.guideId');
      params.parentGuideId = parentGuideId;

      // in order to display correct favorite options in the tuner as well as favorite state
      // we need to fetch the parent profile for topics in cases when a topic is tuned to
      // without loading the profile page (i.e. search)
      if (parentGuideId && !get(state, ['profile', parentGuideId])) {
        dispatch(fetchProfile({ guideId: parentGuideId }));
      }
    }

    fbTrack(fbPixelCustomEvents.StartPlay, {
      guideId: tunedGuideId,
      isAutoplayed,
    });
    dispatch(logPlayStart({ isAutoplayed, guideId: tunedGuideId, itemToken }));
    dispatch(primeTuneForMobileSync());
    dispatch(requestTune(state.player, params));

    mint.updateState('tuneRequested', true);
    // https://tunein.atlassian.net/browse/TUNE-12903
    const dfpUrlParams = buildDfpUrlParams(parentGuideId || tunedGuideId);
    mint.updateState('dfpParams', dfpUrlParams);
    const isAutoPlayingRecents = guideItem === selectAutoPlayGuideItem(state);
    const shouldPreloadPrerollPlayer =
      !isUserSubscribed && !hasMetPrerollQuota && !isAutoPlayingRecents;

    if (isAutoPlayingRecents) {
      const { actions, labels: featureLabel } = feature;
      dispatch(
        logFeatureActivity(actions.autoPlay, featureLabel.playMiniPlayer),
      );
    }

    if (shouldPreloadPrerollPlayer) {
      await mint.preload();
    }

    const loadOptions = {
      formats,
      guideId: tunedGuideId,
      itemToken,
      params: await buildTuneParams(state, dfpUrlParams),
      title: guideItem.title,
      type: getTypeLowerCase(tunedGuideId),
      playFromPosition,
      isUserSubscribed,
      hasMetPrerollQuota,
    };

    try {
      const loadMeta = await tuner.load(loadOptions);

      if (get(loadMeta, 'isHtmlStream')) {
        return;
      }

      if (!isUserSubscribed && !isAutoPlayingRecents) {
        // Stop any currently playing prerolls before (re)attempting preroll / retuning
        mint.stop();

        await attemptPreroll(dispatch, getState());
      } else {
        dispatch(
          logPrerollEligible({ audioEnabled: false, videoEnabled: false }),
        );
      }

      loadAndPlay(dispatch);
    } catch (e) {
      return dispatch(TunerActions.stationFailed());
    }
  };
  /* eslint-enable consistent-return */
}

function retune() {
  return async (dispatch, getState) => {
    const state = getState();
    const playerState = state.player;
    const { tunedGuideId, parentGuideId, itemToken, guideItemPathname } =
      playerState;

    if (!tunedGuideId) {
      return null;
    }

    dispatch(logPlayStart({ guideId: tunedGuideId, itemToken }));
    dispatch(requestTune(playerState, { guideItemPathname, itemToken }));
    mint.updateState('tuneRequested', true);

    // https://tunein.atlassian.net/browse/TUNE-12903
    const dfpUrlParams = buildDfpUrlParams(parentGuideId || tunedGuideId);
    mint.updateState('dfpParams', dfpUrlParams);

    return tuner
      .load({
        guideId: tunedGuideId,
        params: await buildTuneParams(state, dfpUrlParams),
        type: getTypeLowerCase(tunedGuideId),
      })
      .then(() => dispatch(loadAndPlay))
      .catch(() => dispatch(TunerActions.stationFailed()));
  };
}

function setHasAdBlocker(hasAdBlocker) {
  return (dispatch) => dispatch({ type: SET_HAS_ADBLOCKER, hasAdBlocker });
}

function openPopOutPlayer() {
  return { type: OPEN_POPOUT_PLAYER };
}

function setPopoutPlayerStatus() {
  return { type: SET_POPOUT_PLAYER_STATUS };
}

function fetchNowPlaying(guideId) {
  const transform = (resp) => ({ nowPlaying: resp.data.nowPlaying });

  return {
    type: FETCH_NOW_PLAYING,
    api: {
      endpoint: ['profile', 'nowPlaying'],
      args: [guideId],
      transform,
    },
  };
}

function primePlayerForAutoPlay() {
  return async (dispatch, getState) => {
    await dispatch(fetchCategory({ guideId: AUTOPLAY_GUIDE_ID }));
    const autoPlayContainerItems = get(
      selectAutoPlayCategory(getState()),
      'containerItems',
    );
    const autoPlayContainer = find(
      autoPlayContainerItems,
      (item) => item.containerType === AUTOPLAY,
    );
    const lastThreeItems = get(autoPlayContainer, 'children', []).slice(0, 3);
    const latestPlayableItem = find(lastThreeItems, (item) => isPlayable(item));

    if (latestPlayableItem) {
      await dispatch(fetchNowPlaying(latestPlayableItem.guideId));
      return dispatch({
        type: AUTOPLAY_READY,
        meta: {
          guideItem: latestPlayableItem,
          guideItemPathname: getGuideItemPathname(latestPlayableItem),
        },
      });
    }
  };
}

export {
  // action types
  PRE_TUNE,
  REQUEST_TUNE,
  HANDLE_LOAD,
  PLAY,
  PAUSE,
  STOP,
  SEEK,
  SET_VOLUME,
  SET_HAS_ADBLOCKER,
  SET_SHOW_VOLUME_BAR,
  OPEN_POPOUT_PLAYER,
  SET_POPOUT_PLAYER_STATUS,
  UPDATE_LAST_TUNE_REQUEST,
  FETCH_NOW_PLAYING,
  AUTOPLAY_READY,
  // methods
  handlePlay,
  handlePause,
  buildDfpUrlParams,
  buildTuneParams,
  requestTune,
  seek,
  play,
  pause,
  stop,
  setVolume,
  setShowVolumeBar,
  toggleMuteVolume,
  primeTuneForMobileSync,
  loadAndPlay,
  attemptPreroll,
  waitForGdprObligationsToComplete,
  tune,
  retune,
  setHasAdBlocker,
  openPopOutPlayer,
  setPopoutPlayerStatus,
  fetchNowPlaying,
  primePlayerForAutoPlay,
};
